/**
 * Global site configuration object.
 *
 * Contains application metadata, environment-driven URLs, and a complete
 * map of every backend API path.  All paths are relative to `backend.baseURL`
 * and correspond 1-to-1 with the server's `APIRouter.ts` route definitions.
 */

/** API endpoint path definitions grouped by domain. */
interface SiteApi {
	/** GET — app name + version. */
	configuration: string;
	/** POST — password login. Body: `{ email, password }`. */
	login: string;
	/** POST — new account. Body: `{ firstName, lastName, email, password }`. */
	register: string;
	/** POST — Google ID-token login. Body: `{ idToken }`. */
	loginGoogle: string;
	/** POST — Sign in with Apple login. Body: `{ code, firstName?, lastName? }`. */
	loginApple: string;
	/** POST — Sign in with Microsoft login. Body: `{ code }`. */
	loginMicrosoft: string;
	/** POST — request email OTP. Body: `{ email }`. */
	emailOtpRequest: string;
	/** POST — verify email OTP. Body: `{ email, code }`. */
	emailOtpVerify: string;
	/** POST — verify 2FA TOTP during login. Body: `{ tempToken, code }`. */
	twoFactorVerify: string;
	/** POST — initiate password reset. Body: `{ email }`. */
	forgotPassword: string;
	/** POST — validate reset code. Body: `{ email, code }`. */
	resetPasswordValidate: string;
	/** POST — complete password reset. Body: `{ email, code, newPassword }`. */
	resetPassword: string;
	/** POST — verify account email. Body: `{ email, code }`. */
	verifyAccount: string;
	/** POST — request passkey authentication options (unauthenticated). Body: `{ email? }`. */
	passkeyAuthOptions: string;
	/** POST — verify passkey authentication (unauthenticated). Body: `{ challengeKey, response }`. */
	passkeyAuthVerify: string;
	/** POST — refresh JWT. */
	refresh: string;
	/** GET — current user from token. */
	authMe: string;
	/** POST — generate TOTP secret + QR. */
	twoFactorSetup: string;
	/** POST — confirm TOTP setup with code. Body: `{ code }`. */
	twoFactorConfirm: string;
	/** DELETE — disable 2FA. Body: `{ code }`. */
	twoFactorDisable: string;
	/** POST — authorize user for application (consent flow). Body: `{ appUUID }`. */
	authorize: string;
	/** POST — revoke current JWT (logout). */
	logout: string;
	/** POST — revoke all tokens for current user. */
	logoutAll: string;
	/** GET — current user (legacy). */
	userMe: string;
	/** GET — paginated user list (admin). Query: `limit, offset, search`. */
	userList: string;
	/** GET / PUT — single user by ID (admin). */
	user: string;
	/** POST — admin-create a user. Body: `{ email, password, firstName?, lastName?, username?, isActive?, isVerified? }`. */
	userCreate: string;
	/** POST — bulk import dormant users (admin). Body: `{ rows, applicationIds }`. */
	userImport: string;
	/** POST — preview import: check which emails already exist. Body: `{ emails }`. */
	userImportPreview: string;
	/** GET — applications a user is registered to (admin). Path: `/api/user/:id/applications`. */
	userApplications: string;
	/** Downloads every token balance, and optionally the ledger, as a JSON file (admin). */
	tokenExport: string;

	/** Summarises what a token export would contain, without building it (admin). */
	tokenExportSummary: string;

	/** GET / POST — notifications for a user (admin). Path: `/api/user/:id/notifications`. */
	userNotifications: string;
	/** DELETE — revoke all sessions for a user (admin). Path: `/api/user/:id/sessions`. */
	userSessions: string;
	/** GET — paginated audit logs. Query: `limit, offset, action, userId`. */
	auditList: string;
	/** GET / PUT — own profile. */
	profile: string;
	/** PUT — change password. Body: `{ currentPassword, newPassword }`. */
	profilePassword: string;
	/** POST — request email change OTP. Body: `{ newEmail }`. */
	profileEmailRequest: string;
	/** POST — confirm email change. Body: `{ code }`. */
	profileEmailConfirm: string;
	/** GET — check username validity/availability. Query: `?username=`. */
	profileUsernameCheck: string;
	/** POST — request username change OTP. Body: `{ newUsername }`. */
	profileUsernameRequest: string;
	/** POST — confirm username change. Body: `{ code }`. */
	profileUsernameConfirm: string;
	/** POST — request account-deletion OTP (sent to current email). */
	profileDeletionRequest: string;
	/** POST — confirm account deletion. Body: `{ code }`. */
	profileDeletionConfirm: string;
	/** POST — upload avatar (multipart). DELETE — remove avatar. */
	profileAvatar: string;
	/** GET — own notifications. Query: `limit, offset`. */
	profileNotifications: string;
	/** PUT — mark all notifications read. */
	profileNotificationsReadAll: string;
	/** PUT — mark single notification read. Append `/:id/read`. */
	profileNotificationRead: string;
	/** DELETE — delete notification. Append `/:id`. */
	profileNotificationDelete: string;
	/** GET — user's registered applications. */
	profileApplications: string;
	/** GET — list active sessions. */
	profileSessions: string;
	/** DELETE — revoke a specific session. Append `/:id`. */
	profileSession: string;
	/** DELETE — revoke all other sessions. */
	profileSessionsRevokeOthers: string;
	/** GET — list registered passkeys. */
	profilePasskeys: string;
	/** POST — start passkey registration. */
	profilePasskeyRegisterOptions: string;
	/** POST — complete passkey registration. Body: `{ challengeKey, response, friendlyName? }`. */
	profilePasskeyRegisterVerify: string;
	/** PUT — rename a passkey. Append `/:id`. Body: `{ friendlyName }`. */
	profilePasskey: string;
	/** DELETE — delete a passkey. Append `/:id`. */
	profilePasskeyDelete: string;
	/** GET — public application info by UUID. Path: `/api/applications/public/:uuid`. */
	applicationPublic: string;
	/** GET — list all applications. POST — create. */
	applications: string;
	/** GET / PUT / DELETE — single application. Append `/:id`. */
	application: string;
	/** POST — register user to app. Path: `/api/applications/:id/users/:userId`. */
	applicationRegisterUser: string;
	/** PUT — set user permission. Path: `/api/applications/:id/users/:userId/permission`. */
	applicationSetPermission: string;
	/** DELETE — unregister user. Path: `/api/applications/:id/users/:userId`. */
	applicationUnregisterUser: string;
	/** POST — upload application logo. Path: `/api/applications/:id/logo`. */
	applicationUploadLogo: string;
	/** DELETE — remove application logo. Path: `/api/applications/:id/logo`. */
	applicationDeleteLogo: string;
	/** POST — upload application icon. Path: `/api/applications/:id/icon`. */
	applicationUploadIcon: string;
	/** DELETE — remove application icon. Path: `/api/applications/:id/icon`. */
	applicationDeleteIcon: string;
	/** GET — list all roles. POST — create. */
	roles: string;
	/** GET / PUT / DELETE — single role. Append `/:id`. */
	role: string;
	/** GET — roles for a user. Path: `/api/roles/user/:userId`. */
	rolesForUser: string;
	/** PUT — set all roles for user. Path: `/api/roles/user/:userId`. */
	setUserRoles: string;
	/** POST — assign role. Path: `/api/roles/:id/assign/:userId`. */
	roleAssign: string;
	/** DELETE — remove role. Path: `/api/roles/:id/assign/:userId`. */
	roleRemove: string;
	/** GET / PUT — global settings. */
	settings: string;
	/** PUT — update SMTP config. */
	settingsSMTP: string;
	/** POST — send test email. */
	settingsSMTPTest: string;
	/** POST — verify SMTP code. */
	settingsSMTPVerify: string;
	/** POST — upload sidebar link icon. Append `/:index/icon`. */
	settingsSidebarLinkIcon: string;
	/** GET — list templates. POST — create. */
	emailTemplates: string;
	/** GET / PUT / DELETE — single template. Append `/:id`. */
	emailTemplate: string;
	/** POST — preview rendered template. Path: `/api/email-templates/:id/preview`. */
	emailTemplatePreview: string;
	/** GET / POST — placeholders for template. Path: `/api/email-templates/:id/placeholders`. */
	emailTemplatePlaceholders: string;
	/** DELETE — delete placeholder. Path: `/api/email-templates/placeholders/:phId`. */
	emailTemplatePlaceholderDelete: string;
	/** GET — list newsletters. POST — create. */
	newsletters: string;
	/** GET / PUT / DELETE — single newsletter. Append `/:id`. */
	newsletter: string;
	/** POST — send newsletter. Path: `/api/newsletters/:id/send`. */
	newsletterSend: string;
	/** GET — newsletter staging data. Path: `/api/newsletters/:id/staging`. */
	newsletterStaging: string;
	/** POST — pause newsletter. Path: `/api/newsletters/:id/pause`. */
	newsletterPause: string;
	/** POST — resume newsletter. Path: `/api/newsletters/:id/resume`. */
	newsletterResume: string;
	/** POST — cancel newsletter. Path: `/api/newsletters/:id/cancel`. */
	newsletterCancel: string;
	/** GET — newsletter analytics. Path: `/api/newsletters/:id/analytics`. */
	newsletterAnalytics: string;
	/** GET — newsletter recipients. Path: `/api/newsletters/:id/recipients`. */
	newsletterRecipients: string;
	/** POST — preview newsletter. Path: `/api/newsletters/:id/preview`. */
	newsletterPreview: string;
	/** POST — test-send newsletter. Path: `/api/newsletters/:id/test-send`. */
	newsletterTestSend: string;
	/** GET — aggregated dashboard statistics. */
	dashboardStats: string;
	/** POST — dismiss system error alert on the admin dashboard. */
	dashboardDismissErrors: string;
	/** GET — grid-bucketed session locations for the analytics map. */
	dashboardSessionLocations: string;
	/** GET — proxied WordPress news feed items. */
	newsFeed: string;
	/** POST — probe a URL for a WordPress REST API endpoint. Body: `{ url }`. */
	newsFeedProbe: string;
	/** POST — create OAuth authorization code (after consent). */
	oauthAuthorize: string;
	/** POST — exchange code / refresh token for tokens. */
	oauthToken: string;
	/** GET — OIDC UserInfo. */
	oauthUserinfo: string;
	/** POST — revoke OAuth refresh token. */
	oauthRevoke: string;
	/** GET — OIDC discovery. */
	oauthDiscovery: string;
	/** GET — licensing applications with a summary of the caller's standing in each. */
	licensingApps: string;
	/**
	 * Base path for the per-application licensing routes. Every one is built from it:
	 * `/:appUUID/licenses`, `/:appUUID/licenses/:licenseUUID`,
	 * `/:appUUID/licenses/:licenseUUID/activations`, `/:appUUID/licenses/:licenseUUID/seats`
	 * `/:appUUID/activations`, `/:appUUID/downloads` and
	 * `/:appUUID/downloads/:fileRecordUUID/url`.
	 */
	licensing: string;
	/** POST — test a licensing backend (admin). Body: `{ url, adapter? }`. */
	licensingProbe: string;
	/** GET — the caller's support overview: agent flag, products with eligibility, open count. */
	supportOverview: string;
	/** GET — active ticket categories. Query: `applicationUUID`. */
	supportCategories: string;
	/**
	 * Base path of the customer ticket routes: GET (list) / POST (multipart create) here,
	 * then `/:ticketKey`, `/:ticketKey/messages`, `/:ticketKey/close`, `/:ticketKey/reopen`,
	 * `/:ticketKey/rating` and `/:ticketKey/participants` (POST; DELETE appends `/:userUUID`).
	 */
	supportTickets: string;
	/** GET — download one attachment. Append `/:attachmentUUID`. */
	supportAttachments: string;
	/** DELETE — erase one attachment, file and all (agents). Append `/:attachmentUUID`. */
	supportDeskAttachments: string;
	/** GET — the agent desk's headline counts (agents). */
	supportDeskStats: string;
	/** GET — every support agent, for assignment (agents). */
	supportDeskAgents: string;
	/**
	 * Base path of the agent ticket routes (agents): GET (queue) / POST (create on behalf)
	 * here, `/bulk`, then `/:ticketKey` (GET / PATCH), `/:ticketKey/messages` and
	 * `/:ticketKey/participants` (POST; DELETE appends `/:userUUID`).
	 */
	supportDeskTickets: string;
	/** GET / POST — reply templates; PUT / DELETE append `/:templateUUID` (agents). */
	supportReplyTemplates: string;
	/** GET / PUT — support desk settings (admin). */
	supportAdminSettings: string;
	/** GET / POST — every ticket category, inactive included; PUT / DELETE append `/:categoryUUID` (admin). */
	supportAdminCategories: string;
	/**
	 * GET — every routing rule with at least one member (admin). PUT `/:applicationUUID` replaces
	 * one rule — the product rule, or a category × product rule — named by the body's `categoryUUID`.
	 */
	supportAdminRouting: string;
	/** GET — raw tracked-action event stream (admin). Query: `limit, offset, query`. */
	analyticsEvents: string;
	/** GET — the metric catalogue: metrics, dimensions, granularities, operators (admin). */
	analyticsCatalog: string;
	/** POST — run one analytics query (admin). Body: the query. */
	analyticsQuery: string;
	/** POST — run a whole page of analytics queries in one request (admin). */
	analyticsBatch: string;
	/** GET — distinct values of a metric field, for filter autocomplete (admin). */
	analyticsValues: string;
	/** GET — monthly cohort-retention matrix (admin). Query: `days, maxOffset`. */
	analyticsCohort: string;
	/** GET / POST — the shared analytics dashboard configuration (admin). */
	analyticsDashboard: string;
	/** GET / POST — analytics collection settings: retention and the tracking toggle (admin). */
	analyticsConfiguration: string;
}

/** Backend connection configuration. */
interface SiteBackend {
	/** Base URL prefix for all API calls (empty string = same origin). */
	baseURL: string;
	/** Every API endpoint path. */
	api: SiteApi;
}

/** Global site configuration. */
interface SiteConfig {
	/** Full application name shown in UI chrome. */
	name: string;
	/** Abbreviated label used in compact spaces (sidebar footer, badges). */
	shortname: string;
	/** Root href for the navbar brand link. */
	href: string;
	/** Origin URL of this client deployment. */
	baseURL: string;
	/** Backend connection settings. */
	backend: SiteBackend;
}

const site: SiteConfig = {
	/** Full application name shown in UI chrome. */
	name: 'Abstract ID',
	/** Abbreviated label used in compact spaces (sidebar footer, badges). */
	shortname: 'Abstract ID',
	/** Root href for the navbar brand link. */
	href: '/',
	/** Origin URL of this client deployment. */
	baseURL: import.meta.env.VITE_ABSTRACTID_BASE_URL || window.location.origin,

	backend: {
		/** Base URL prefix for all API calls (empty string = same origin). */
		baseURL: import.meta.env.VITE_ABSTRACTID_BACKEND_URL || '',

		/** Every API endpoint path, grouped by domain. */
		api: {
			// ── Public ────────────────────────────────────────────
			/** GET — app name + version. */
			configuration: '/api/configuration',

			// ── Auth (rate-limited, unauthenticated) ──────────────
			/** POST — password login. Body: `{ email, password }`. */
			login: '/api/auth/login',
			/** POST — new account. Body: `{ firstName, lastName, email, password }`. */
			register: '/api/auth/register',
			/** POST — Google ID-token login. Body: `{ idToken }`. */
			loginGoogle: '/api/auth/google',
			/** POST — Sign in with Apple login. Body: `{ code, firstName?, lastName? }`. */
			loginApple: '/api/auth/apple',
			/** POST — Sign in with Microsoft login. Body: `{ code }`. */
			loginMicrosoft: '/api/auth/microsoft',
			/** POST — request email OTP. Body: `{ email }`. */
			emailOtpRequest: '/api/auth/email-otp/request',
			/** POST — verify email OTP. Body: `{ email, code }`. */
			emailOtpVerify: '/api/auth/email-otp/verify',
			/** POST — verify 2FA TOTP during login. Body: `{ tempToken, code }`. */
			twoFactorVerify: '/api/auth/2fa/verify',
			/** POST — initiate password reset. Body: `{ email }`. */
			forgotPassword: '/api/auth/forgot-password',
			/** POST — validate reset code. Body: `{ email, code }`. */
			resetPasswordValidate: '/api/auth/reset-password/validate',
			/** POST — complete password reset. Body: `{ email, code, newPassword }`. */
			resetPassword: '/api/auth/reset-password',
			/** POST — verify account email. Body: `{ email, code }`. */
			verifyAccount: '/api/auth/verify-account',
			/** POST — request passkey authentication options (unauthenticated). Body: `{ email? }`. */
			passkeyAuthOptions: '/api/auth/passkey/options',
			/** POST — verify passkey authentication (unauthenticated). Body: `{ challengeKey, response }`. */
			passkeyAuthVerify: '/api/auth/passkey/verify',

			// ── Auth (authenticated) ──────────────────────────────
			/** POST — refresh JWT. */
			refresh: '/api/auth/refresh',
			/** GET — current user from token. */
			authMe: '/api/auth/me',
			/** POST — generate TOTP secret + QR. */
			twoFactorSetup: '/api/auth/2fa/setup',
			/** POST — confirm TOTP setup with code. Body: `{ code }`. */
			twoFactorConfirm: '/api/auth/2fa/confirm',
			/** DELETE — disable 2FA. Body: `{ code }`. */
			twoFactorDisable: '/api/auth/2fa',
			/** POST — authorize user for application (consent flow). Body: `{ appUUID }`. */
			authorize: '/api/auth/authorize',
			/** POST — revoke current JWT (logout). */
			logout: '/api/auth/logout',
			/** POST — revoke all tokens for current user. */
			logoutAll: '/api/auth/logout-all',

			// ── User (legacy compat endpoints) ────────────────────
			/** GET — current user (legacy). */
			userMe: '/api/user/me',
			/** GET — paginated user list (admin). Query: `limit, offset, search`. */
			userList: '/api/user/list',
			/** GET / PUT — single user by ID (admin). */
			user: '/api/user',
			/** POST — admin-create a user. */
			userCreate: '/api/user',
			/** POST — bulk import dormant users (admin). Body: `{ rows, applicationIds }`. */
			userImport: '/api/user/import',
			/** POST — preview import: check which emails already exist. Body: `{ emails }`. */
			userImportPreview: '/api/user/import/preview',
			/** GET — applications a user is registered to (admin). Path: `/api/user/:id/applications`. */
			userApplications: '/api/user',
			/** GET — downloads every token balance, and optionally the ledger, as a JSON file (admin). */
			tokenExport: '/api/user/tokens/export',
			/** GET — summarises what a token export would contain, without building it (admin). */
			tokenExportSummary: '/api/user/tokens/export/summary',
			/** GET / POST — notifications for a user (admin). Path: `/api/user/:id/notifications`. */
			userNotifications: '/api/user',
			/** DELETE — revoke all sessions for a user (admin). Path: `/api/user/:id/sessions`. */
			userSessions: '/api/user',

			// ── Audit (admin) ─────────────────────────────────────
			/** GET — paginated audit logs. Query: `limit, offset, action, userId`. */
			auditList: '/api/audit/list',

			// ── Profile (authenticated) ───────────────────────────
			/** GET / PUT — own profile. */
			profile: '/api/profile',
			/** PUT — change password. Body: `{ currentPassword, newPassword }`. */
			profilePassword: '/api/profile/password',
			/** POST — request email change OTP. Body: `{ newEmail }`. */
			profileEmailRequest: '/api/profile/email/request',
			/** POST — confirm email change. Body: `{ code }`. */
			profileEmailConfirm: '/api/profile/email/confirm',
			/** GET — check username validity/availability. Query: `?username=`. */
			profileUsernameCheck: '/api/profile/username/check',
			/** POST — request username change OTP. Body: `{ newUsername }`. */
			profileUsernameRequest: '/api/profile/username/request',
			/** POST — confirm username change. Body: `{ code }`. */
			profileUsernameConfirm: '/api/profile/username/confirm',
			/** POST — request account-deletion OTP (sent to current email). */
			profileDeletionRequest: '/api/profile/account/deletion/request',
			/** POST — confirm account deletion. Body: `{ code }`. */
			profileDeletionConfirm: '/api/profile/account/deletion/confirm',
			/** POST — upload avatar (multipart). DELETE — remove avatar. */
			profileAvatar: '/api/profile/avatar',
			/** GET — own notifications. Query: `limit, offset`. */
			profileNotifications: '/api/profile/notifications',
			/** PUT — mark all notifications read. */
			profileNotificationsReadAll: '/api/profile/notifications/read-all',
			/** PUT — mark single notification read. Append `/:id/read`. */
			profileNotificationRead: '/api/profile/notifications',
			/** DELETE — delete notification. Append `/:id`. */
			profileNotificationDelete: '/api/profile/notifications',
			/** GET — user's registered applications. */
			profileApplications: '/api/profile/applications',
			/** GET — list active sessions. */
			profileSessions: '/api/profile/sessions',
			/** DELETE — revoke a specific session. Append `/:id`. */
			profileSession: '/api/profile/sessions',
			/** DELETE — revoke all other sessions. */
			profileSessionsRevokeOthers: '/api/profile/sessions',
			/** GET — list registered passkeys. */
			profilePasskeys: '/api/profile/passkeys',
			/** POST — start passkey registration. */
			profilePasskeyRegisterOptions: '/api/profile/passkeys/register/options',
			/** POST — complete passkey registration. Body: `{ challengeKey, response, friendlyName? }`. */
			profilePasskeyRegisterVerify: '/api/profile/passkeys/register/verify',
			/** PUT — rename a passkey. Append `/:id`. Body: `{ friendlyName }`. */
			profilePasskey: '/api/profile/passkeys',
			/** DELETE — delete a passkey. Append `/:id`. */
			profilePasskeyDelete: '/api/profile/passkeys',

			// ── Applications (admin) ──────────────────────────────
			/** GET — public application info by UUID. Path: `/api/applications/public/:uuid`. */
			applicationPublic: '/api/applications/public',
			/** GET — list all applications. POST — create. */
			applications: '/api/applications',
			/** GET / PUT / DELETE — single application. Append `/:id`. */
			application: '/api/applications',
			/** POST — register user to app. Path: `/api/applications/:id/users/:userId`. */
			applicationRegisterUser: '/api/applications',
			/** PUT — set user permission. Path: `/api/applications/:id/users/:userId/permission`. */
			applicationSetPermission: '/api/applications',
			/** DELETE — unregister user. Path: `/api/applications/:id/users/:userId`. */
			applicationUnregisterUser: '/api/applications',
			/** POST — upload application logo. Path: `/api/applications/:id/logo`. */
			applicationUploadLogo: '/api/applications',
			/** DELETE — remove application logo. Path: `/api/applications/:id/logo`. */
			applicationDeleteLogo: '/api/applications',
			/** POST — upload application icon. Path: `/api/applications/:id/icon`. */
			applicationUploadIcon: '/api/applications',
			/** DELETE — remove application icon. Path: `/api/applications/:id/icon`. */
			applicationDeleteIcon: '/api/applications',

			// ── Roles (admin) ─────────────────────────────────────
			/** GET — list all roles. POST — create. */
			roles: '/api/roles',
			/** GET / PUT / DELETE — single role. Append `/:id`. */
			role: '/api/roles',
			/** GET — roles for a user. Path: `/api/roles/user/:userId`. */
			rolesForUser: '/api/roles/user',
			/** PUT — set all roles for user. Path: `/api/roles/user/:userId`. */
			setUserRoles: '/api/roles/user',
			/** POST — assign role. Path: `/api/roles/:id/assign/:userId`. */
			roleAssign: '/api/roles',
			/** DELETE — remove role. Path: `/api/roles/:id/assign/:userId`. */
			roleRemove: '/api/roles',

			// ── Settings (admin) ──────────────────────────────────
			/** GET / PUT — global settings. */
			settings: '/api/settings',
			/** PUT — update SMTP config. */
			settingsSMTP: '/api/settings/smtp',
			/** POST — send test email. */
			settingsSMTPTest: '/api/settings/smtp/test',
			/** POST — verify SMTP code. */
			settingsSMTPVerify: '/api/settings/smtp/verify',
			/** POST — upload sidebar link icon. Append `/:index/icon`. */
			settingsSidebarLinkIcon: '/api/settings/sidebar-links',

			// ── Email Templates (admin) ───────────────────────────
			/** GET — list templates. POST — create. */
			emailTemplates: '/api/email-templates',
			/** GET / PUT / DELETE — single template. Append `/:id`. */
			emailTemplate: '/api/email-templates',
			/** POST — preview rendered template. Path: `/api/email-templates/:id/preview`. */
			emailTemplatePreview: '/api/email-templates',
			/** GET / POST — placeholders for template. Path: `/api/email-templates/:id/placeholders`. */
			emailTemplatePlaceholders: '/api/email-templates',
			/** DELETE — delete placeholder. Path: `/api/email-templates/placeholders/:phId`. */
			emailTemplatePlaceholderDelete: '/api/email-templates/placeholders',

			// ── Newsletters (admin) ───────────────────────────────
			/** GET — list newsletters. POST — create. */
			newsletters: '/api/newsletters',
			/** GET / PUT / DELETE — single newsletter. Append `/:id`. */
			newsletter: '/api/newsletters',
			/** POST — send newsletter. Path: `/api/newsletters/:id/send`. */
			newsletterSend: '/api/newsletters',
			/** GET — newsletter staging data. Path: `/api/newsletters/:id/staging`. */
			newsletterStaging: '/api/newsletters',
			/** POST — pause newsletter. Path: `/api/newsletters/:id/pause`. */
			newsletterPause: '/api/newsletters',
			/** POST — resume newsletter. Path: `/api/newsletters/:id/resume`. */
			newsletterResume: '/api/newsletters',
			/** POST — cancel newsletter. Path: `/api/newsletters/:id/cancel`. */
			newsletterCancel: '/api/newsletters',
			/** GET — newsletter analytics. Path: `/api/newsletters/:id/analytics`. */
			newsletterAnalytics: '/api/newsletters',
			/** GET — newsletter recipients. Path: `/api/newsletters/:id/recipients`. */
			newsletterRecipients: '/api/newsletters',
			/** POST — preview newsletter. Path: `/api/newsletters/:id/preview`. */
			newsletterPreview: '/api/newsletters',
			/** POST — test-send newsletter. Path: `/api/newsletters/:id/test-send`. */
			newsletterTestSend: '/api/newsletters',

			// ── Dashboard (admin) ─────────────────────────────────
			/** GET — aggregated dashboard statistics. */
			dashboardStats: '/api/dashboard/stats',
			/** POST — dismiss system error alert. */
			dashboardDismissErrors: '/api/dashboard/dismiss-errors',
			/** GET — grid-bucketed session locations for the analytics map. */
			dashboardSessionLocations: '/api/dashboard/session-locations',
			/** GET — proxied WordPress news feed items. */
			newsFeed: '/api/news-feed',
			/** POST — probe a URL for a WordPress REST API endpoint. */
			newsFeedProbe: '/api/settings/news-feed/probe',

			// ── OAuth 2.0 / OIDC ──────────────────────────────────
			/** POST — create OAuth authorization code (after consent). */
			oauthAuthorize: '/oauth/authorize',
			/** POST — exchange code / refresh token for tokens. */
			oauthToken: '/oauth/token',
			/** GET — OIDC UserInfo. */
			oauthUserinfo: '/oauth/userinfo',
			/** POST — revoke OAuth refresh token. */
			oauthRevoke: '/oauth/revoke',
			/** GET — OIDC discovery. */
			oauthDiscovery: '/.well-known/openid-configuration',

			// ── Licensing (self-service) ──────────────────────────
			/** GET — licensing applications with a summary of the caller's standing in each. */
			licensingApps: '/api/licensing/apps',
			/** Base path for the per-application licensing routes. */
			licensing: '/api/licensing',
			/** POST — test a licensing backend (admin). Body: `{ url, adapter? }`. */
			licensingProbe: '/api/licensing/probe',

			// ── Support Desk ──────────────────────────────────────
			/** GET — the caller's support overview. */
			supportOverview: '/api/support/overview',
			/** GET — active ticket categories. */
			supportCategories: '/api/support/categories',
			/** Base path of the customer ticket routes. */
			supportTickets: '/api/support/tickets',
			/** GET — download one attachment. */
			supportAttachments: '/api/support/attachments',
			/** GET — the agent desk's headline counts (agents). */
			supportDeskStats: '/api/support/desk/stats',
			/** GET — every support agent (agents). */
			supportDeskAgents: '/api/support/desk/agents',
			supportDeskAttachments: '/api/support/desk/attachments',
			/** Base path of the agent ticket routes (agents). */
			supportDeskTickets: '/api/support/desk/tickets',
			/** Reply templates (agents). */
			supportReplyTemplates: '/api/support/desk/templates',
			/** Support desk settings (admin). */
			supportAdminSettings: '/api/support/admin/settings',
			/** Every ticket category (admin). */
			supportAdminCategories: '/api/support/admin/categories',
			/** Ticket routing — who is notified and who is auto-assigned (admin). */
			supportAdminRouting: '/api/support/admin/routing',

			/** GET — raw tracked-action event stream (admin). */
			analyticsEvents: '/api/analytics/events',
			/** GET — the metric catalogue (admin). */
			analyticsCatalog: '/api/analytics/metrics/catalog',
			/** POST — run one analytics query (admin). */
			analyticsQuery: '/api/analytics/metrics/query',
			/** POST — run a whole page of analytics queries in one request (admin). */
			analyticsBatch: '/api/analytics/metrics/batch',
			/** GET — distinct values of a metric field (admin). */
			analyticsValues: '/api/analytics/metrics/values',
			/** GET — monthly cohort-retention matrix (admin). */
			analyticsCohort: '/api/analytics/cohort',
			/** GET / POST — the shared analytics dashboard configuration (admin). */
			analyticsDashboard: '/api/analytics/dashboard',
			/** GET / POST — analytics collection settings (admin). */
			analyticsConfiguration: '/api/analytics/configuration'
		}
	}
};

/** Global site configuration singleton. */
export default site;
